import 'firebase/auth';
import firebase from 'firebase/app';
import { auth } from './firebase';
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import crud from '../api/crud';
import { isTrashieApp } from '../config/config';
import { EVENT_STREAM_APP_TYPES } from '../components/trashie/config/service';

export const signIn = async (email, password) => {
  await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
  await auth.signInWithEmailAndPassword(email, password);
};

export const signInWithGoogle = async () => {
  const googleProvider = new firebase.auth.GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account',
  });

  const user = await auth.signInWithPopup(googleProvider);

  return user;
};

export const signInWithFacebook = async () => {
  const facebookProvider = new firebase.auth.FacebookAuthProvider();
  await auth.signInWithPopup(facebookProvider);
};

export const signOut = async () => {
  await auth.signOut();
};

export const createNewUser = async (email, password) => {
  const user = await auth.createUserWithEmailAndPassword(email, password);

  try {
    await crud.post({
      path: '/users',
      body: {
        data: {
          email,
          uid: user.user.uid,
          meta: {
            migrated: false,
            application: isTrashieApp() ? EVENT_STREAM_APP_TYPES.TRASHIE : '',
          },
        },
      },
    });
  } catch (e) {
    // do nothing
  }
  return user;
};

export const sendPasswordResetEmail = async (email) => {
  await auth.sendPasswordResetEmail(email);
};

export const getFirebaseToken = async () => {
  if (firebase.auth().currentUser) {
    await firebase.auth().currentUser.getIdToken();
  }
};

export const currentUser = async () => {
  await firebase.auth().currentUser.currentUser();
};

export const saveCurrentAuthToken = async () => {
  const { currentUser: user } = firebase.auth();

  if (!user) {
    throw new Error('No authenticated user');
  }

  return user.getIdToken();
};

let recaptchaVerifier = null;
// Cached widget ID from the first render. Using the cached value on reuse avoids
// calling render() a second time, which can throw "already rendered" in some
// Firebase SDK versions.
let recaptchaWidgetId = null;

export const sendPhoneOtp = async (phoneNumber) => {
  if (!recaptchaVerifier) {
    recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      'recaptcha-container',
      { size: 'invisible' },
    );
    recaptchaWidgetId = null;
  } else if (window.grecaptcha) {
    if (recaptchaWidgetId !== null) {
      // Widget ID already cached — reset directly, no render() call needed.
      window.grecaptcha.reset(recaptchaWidgetId);
    } else {
      // grecaptcha available but no cached ID yet — render once to get it.
      recaptchaWidgetId = await recaptchaVerifier.render();
      window.grecaptcha.reset(recaptchaWidgetId);
    }
  } else {
    // grecaptcha not yet available — recreate the verifier to avoid stale state.
    try { recaptchaVerifier.clear(); } catch { /* ignore */ }
    recaptchaWidgetId = null;
    recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      'recaptcha-container',
      { size: 'invisible' },
    );
  }

  const confirmationResult = await auth.signInWithPhoneNumber(phoneNumber, recaptchaVerifier);

  // After the first signInWithPhoneNumber the widget is rendered internally.
  // Cache its ID now so subsequent reuse calls never need to call render() at all.
  if (recaptchaWidgetId === null && window.grecaptcha) {
    try { recaptchaWidgetId = await recaptchaVerifier.render(); } catch { /* ignore */ }
  }

  return confirmationResult;
};

export const clearRecaptchaVerifier = () => {
  if (recaptchaVerifier) {
    try { recaptchaVerifier.clear(); } catch { /* ignore */ }
    recaptchaVerifier = null;
  }
  recaptchaWidgetId = null;
};

export const confirmPhoneOtp = async (confirmationResult, otpCode) => {
  const result = await confirmationResult.confirm(otpCode);
  const idToken = await result.user.getIdToken();

  return idToken;
};

export const restoreAuthSession = async (customToken) => {
  await auth.signInWithCustomToken(customToken);
};

export const fetchUserInfo = async (userId) => {
  try {
    const userRef = firebase.firestore().collection('users').doc(userId);

    return userRef.get().then((doc) => {
      if (doc.exists) {
        return doc.data();
      }
      return {};
    }).catch((error) => error);
  } catch (error) {
    return error;
  }
};
